import { GetBranchIOShareUrlParams } from 'api/branch/types';
import { USER_PSEUDO_ID_LOADER_KEY } from 'components/UserPseudoIdLoader';
import { useFirebaseAuth } from 'hooks/useFirebaseAuth';
import React, {
  createContext,
  lazy,
  PropsWithChildren,
  Suspense,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import queryString from 'query-string';
import { mutate } from 'swr';

const TrackDeepLinkOpenEvent = lazy(
  () => import('../../components/TrackDeepLinkOpenEvent')
);

interface Context {
  branchInitialized: boolean;
  generateBranchUrl: ({ cb, data }: GetBranchIOShareUrlParams) => void;
  Branch?: typeof import('../../api/branch');
}

const BranchContext = createContext<Context>({
  branchInitialized: false,
  generateBranchUrl: () => {}
});

export const BranchProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { userPseudoId, user } = useFirebaseAuth();
  const [Branch, setBranch] = useState<typeof import('../../api/branch')>();
  const [isShowEventTrackComponent, setIsShowEventTrackComponent] =
    useState(false);

  const metaRet = useRef<{
    branchInitialized: boolean;
    BranchMod?: typeof import('../../api/branch');
    initPromise?: Promise<void>;
  }>({
    branchInitialized: false
  });

  // Load Branch on mount
  useEffect(() => {
    let mounted = true;
    import('../../api/branch').then(mod => {
      if (mounted) {
        metaRet.current.BranchMod = mod;
        setBranch(mod);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  const loadBranch = React.useCallback(async () => {
    if (metaRet.current.BranchMod) {
      return metaRet.current.BranchMod;
    }

    const mod = await import('../../api/branch');

    metaRet.current.BranchMod = mod;
    setBranch(mod);
    return mod;
  }, []);

  const initBranch = React.useCallback(async () => {
    const BranchMod = await loadBranch();

    if (metaRet.current.branchInitialized) {
      return;
    }

    if (!metaRet.current.initPromise) {
      metaRet.current.initPromise = new Promise<void>(resolve => {
        BranchMod.init(branchData => {
          metaRet.current.branchInitialized = true;
          setIsShowEventTrackComponent(true);

          try {
            BranchMod.saveUTMs(window.location, branchData);
          } catch (error) {
            console.warn('branch.saveUTM: ERROR', error);
          } finally {
            resolve();
          }
        });
      }).finally(() => {
        metaRet.current.initPromise = undefined;
      });
    }

    await metaRet.current.initPromise;
  }, [loadBranch]);

  const generateBranchUrl = React.useCallback(
    async ({ cb, data }: GetBranchIOShareUrlParams) => {
      if (!metaRet.current.branchInitialized) {
        await initBranch();
      }

      const Branch = await loadBranch();
      let pseudoId = userPseudoId;

      if (!pseudoId) {
        pseudoId = await mutate(USER_PSEUDO_ID_LOADER_KEY);
      }

      Branch.getBranchIOShareUrl({
        cb,
        data: {
          linkType: 'web_install_app',
          appVersion: process.env.REACT_APP_VERSION,
          utmMedium: 'public-website',
          campaign: 'web-share',
          sharerUserId: user?.uid,
          sharerUserPseudoId: pseudoId,
          ...data
        }
      });
    },
    [initBranch, loadBranch, user?.uid, userPseudoId]
  );

  useEffect(() => {
    const params = queryString.parse(window.location.search);
    const branchMatchId = params._branch_match_id;

    if (branchMatchId) {
      initBranch().catch(error => {
        console.warn('branch.init: ERROR', error);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BranchContext.Provider
      value={{
        branchInitialized: metaRet.current.branchInitialized,
        generateBranchUrl,
        Branch
      }}
    >
      {children}

      {isShowEventTrackComponent && (
        <Suspense fallback={null}>
          <TrackDeepLinkOpenEvent />
        </Suspense>
      )}
    </BranchContext.Provider>
  );
};

export const useBranch = () => {
  const context = useContext(BranchContext);

  if (!context) {
    throw new Error('useBranch needs to be used within BranchProvider');
  }

  return context;
};
